import React, { useEffect, useState, Children } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate, useLocation } from 'react-router-dom';
import { LanguageProvider } from './i18n';
import { HomePage } from './pages/HomePage';
import { ServicesPage } from './pages/ServicesPage';
import { ServiceDetailPage } from './pages/ServiceDetailPage';
import { AboutPage } from './pages/AboutPage';
import { ContactPage } from './pages/ContactPage';
import { PricesPage } from './pages/PricesPage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { CookiePolicyPage } from './pages/CookiePolicyPage';
import { PatientPrivacyNoticePage } from './pages/PatientPrivacyNoticePage';
import { TermsAndConditionsPage } from './pages/TermsAndConditionsPage';
import { BusinessCardPage } from './pages/BusinessCardPage';
import { BusinessCardNewPage } from './pages/BusinessCardNewPage';
import { BusinessCardNewUaPage } from './pages/BusinessCardNewUaPage';
import { BusinessCardNewRuPage } from './pages/BusinessCardNewRuPage';
import { UkrainianDentistPage } from './pages/UkrainianDentistPage';
import { ImplantyLondonPage } from './pages/ImplantyLondonPage';
import { VeneersLondonPage } from './pages/VeneersLondonPage';
import { RootCanalLondonPage } from './pages/RootCanalLondonPage';
import { EmergencyDentistLondonPage } from './pages/EmergencyDentistLondonPage';
import { RussianDentistPage } from './pages/RussianDentistPage';
import { ImplantyLondonRuPage } from './pages/ImplantyLondonRuPage';
import { VeneersLondonRuPage } from './pages/VeneersLondonRuPage';
import { RootCanalLondonRuPage } from './pages/RootCanalLondonRuPage';
import { EmergencyDentistLondonRuPage } from './pages/EmergencyDentistLondonRuPage';
import { BlogIndexPage } from './pages/blog/BlogIndexPage';
import { ImplantyCostArticle } from './pages/blog/ImplantyCostArticle';
import { NhsVsPrivateArticle } from './pages/blog/NhsVsPrivateArticle';
import { WithoutInsuranceArticle } from './pages/blog/WithoutInsuranceArticle';
import { ToothPainArticle } from './pages/blog/ToothPainArticle';
import { VeneersGuideArticle } from './pages/blog/VeneersGuideArticle';
import { ChooseDentistArticle } from './pages/blog/ChooseDentistArticle';
import { SitemapPage } from './pages/SitemapPage';
import { OrthodonticsPage } from './pages/OrthodonticsPage';
import { ClearAlignersPage } from './pages/ClearAlignersPage';
import { InvisalignAlignersPage } from './pages/InvisalignAlignersPage';
import { MetalBracesPage } from './pages/MetalBracesPage';
import { CeramicBracesPage } from './pages/CeramicBracesPage';
import { RetainersPage } from './pages/RetainersPage';
import { ChildrensOrthodonticsPage } from './pages/ChildrensOrthodonticsPage';
import { OrthodonticAppliancesPage } from './pages/OrthodonticAppliancesPage';
import { GiftCertificate } from './components/GiftCertificate';
import { PrintPricePage } from './pages/PrintPricePage';
import { InfluencerInvitePage } from './pages/InfluencerInvitePage';
import { RaffleVoucherPage } from './pages/RaffleVoucherPage';
import { LogoPage } from './pages/LogoPage';
import { OrthodonticFlyerUaPage } from './pages/OrthodonticFlyerUaPage';
import { PaediatricDentistryPage } from './pages/PaediatricDentistryPage';
import { WhatsAppQrPage } from './pages/WhatsAppQrPage';
import { PatientExperiencePage } from './pages/PatientExperiencePage';
import { ResultsPage } from './pages/ResultsPage';
import { ReviewsPage } from './pages/ReviewsPage';
import { AiSupportPage } from './pages/AiSupportPage';
// ── Global scroll-to-top on every route change ──
// Exception: /prices#anchor — let PricesPage handle its own hash scrolling
function ScrollToTop() {
  const {
    pathname,
    hash
  } = useLocation();
  useEffect(() => {
    // Disable browser scroll restoration globally
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);
  useEffect(() => {
    // If navigating to /prices with a hash, skip — PricesPage handles it
    const isPricesWithHash = pathname.includes('/prices') && hash;
    if (isPricesWithHash) return;
    // Immediate scroll
    window.scrollTo(0, 0);
    // Reliable delayed scroll for dynamic content / mobile
    requestAnimationFrame(() => {
      window.scrollTo(0, 0);
    });
    const t = setTimeout(() => {
      window.scrollTo(0, 0);
    }, 50);
    return () => clearTimeout(t);
  }, [pathname, hash]);
  return null;
}
// Detect user's preferred language from browser
function detectUserLanguage(): 'en' | 'ua' | 'ru' | 'ro' {
  const browserLangs = navigator.languages || [navigator.language];
  for (const lang of browserLangs) {
    const langCode = lang.toLowerCase().split('-')[0];
    if (langCode === 'uk' || langCode === 'ua') {
      return 'ua';
    }
    if (langCode === 'ru') {
      return 'ru';
    }
    if (langCode === 'ro') {
      return 'ro';
    }
    if (langCode === 'en') {
      return 'en';
    }
  }
  return 'en';
}
function getStoredLanguage(): 'en' | 'ua' | 'ru' | 'ro' | null {
  try {
    const stored = localStorage.getItem('dentico-preferred-lang');
    if (stored === 'en' || stored === 'ua' || stored === 'ru' || stored === 'ro') {
      return stored as 'en' | 'ua' | 'ru' | 'ro';
    }
  } catch {
    // localStorage not available
  }
  return null;
}
function setStoredLanguage(lang: 'en' | 'ua' | 'ru' | 'ro') {
  try {
    localStorage.setItem('dentico-preferred-lang', lang);
  } catch {
    // localStorage not available
  }
}
function LanguageRedirect() {
  const navigate = useNavigate();
  useEffect(() => {
    const storedLang = getStoredLanguage();
    if (storedLang) {
      navigate(`/${storedLang}`, {
        replace: true
      });
      return;
    }
    const detectedLang = detectUserLanguage();
    setStoredLanguage(detectedLang);
    navigate(`/${detectedLang}`, {
      replace: true
    });
  }, [navigate]);
  return null;
}
function LanguageRoutes() {
  const {
    lang
  } = useParams();
  const validLangs = ['en', 'ua', 'ru', 'ro'];
  if (!validLangs.includes(lang || '')) {
    return <Navigate to="/en" replace data-id="element-0" data-mp="21tohs0mxgt6r" />;
  }
  useEffect(() => {
    if (lang && validLangs.includes(lang)) {
      setStoredLanguage(lang as 'en' | 'ua' | 'ru' | 'ro');
    }
  }, [lang]);
  return <LanguageProvider data-id="element-1" data-mp="2atjxmirfhcd4">
      <Routes data-id="element-2" data-mp="2apw61p5jn011">
        <Route index element={<HomePage data-id="element-4" data-mp="25sw6zy742xjf" />} data-id="element-3" data-mp="25tfeekz4y3j7" />
        <Route path="ukrainskyi-stomatolog-london" element={<UkrainianDentistPage data-id="element-6" data-mp="2kpn05pq6zd0l" />} data-id="element-5" data-mp="25rm7r5oivjb7" />
        <Route path="implanty-london" element={<ImplantyLondonPage data-id="element-8" data-mp="2frldyvxgyxkw" />} data-id="element-7" data-mp="2kolk16ubk0c0" />
        <Route path="veneers-london" element={<VeneersLondonPage data-id="element-10" data-mp="2fpit0qb6bun1" />} data-id="element-9" data-mp="2fqk42us6wh4r" />
        <Route path="root-canal-london" element={<RootCanalLondonPage data-id="element-12" data-mp="2ti8gz0a9j9ms" />} data-id="element-11" data-mp="2tj9q2z7pxm1v" />
        <Route path="emergency-dentist-london" element={<EmergencyDentistLondonPage data-id="element-14" data-mp="2phogpnue7pwf" />} data-id="element-13" data-mp="2ph50qcoazbl1" />
        <Route path="russkij-stomatolog-london" element={<RussianDentistPage data-id="element-16" data-mp="33eti3aj5w0lc" />} data-id="element-15" data-mp="33gegjb8lk3oh" />
        <Route path="implantaciya-london" element={<ImplantyLondonRuPage data-id="element-18" data-mp="2yjnzoloyx420" />} data-id="element-17" data-mp="2yime3nabsz61" />
        <Route path="viniry-london" element={<VeneersLondonRuPage data-id="element-20" data-mp="3i4o6aqi4rdmx" />} data-id="element-19" data-mp="3i3wd1mc09rzt" />
        <Route path="lechenie-kanalov-london" element={<RootCanalLondonRuPage data-id="element-22" data-mp="3n1eopkheqlbi" />} data-id="element-21" data-mp="3i1jvvbe2nlnt" />
        <Route path="neotlozhnaya-stomatologiya-london" element={<EmergencyDentistLondonRuPage data-id="element-24" data-mp="385762olndryc" />} data-id="element-23" data-mp="3n0d8z2pg5ang" />
        <Route path="blog" element={<BlogIndexPage data-id="element-26" data-mp="3d38kuxc1j540" />} data-id="element-25" data-mp="3d40gu99jv4ut" />
        <Route path="blog/dental-implants-cost-london" element={<ImplantyCostArticle data-id="element-28" data-mp="2zbkd168tr2su" />} data-id="element-27" data-mp="3d6mpydc39sf5" />
        <Route path="blog/nhs-vs-private-dentistry-uk" element={<NhsVsPrivateArticle data-id="element-30" data-mp="345yb5gevfo17" />} data-id="element-29" data-mp="343vck7znkmkv" />
        <Route path="blog/dental-treatment-uk-without-insurance" element={<WithoutInsuranceArticle data-id="element-32" data-mp="2udskqb49peo4" />} data-id="element-31" data-mp="2pfh9bvybqw3s" />
        <Route path="blog/toothache-london-what-to-do" element={<ToothPainArticle data-id="element-34" data-mp="2ge10vtr2l427" />} data-id="element-33" data-mp="2uek5ss08nu05" />
        <Route path="blog/veneers-london-everything-you-need-to-know" element={<VeneersGuideArticle data-id="element-36" data-mp="2kfcq3y617n9v" />} data-id="element-35" data-mp="2gf2lopxw14x4" />
        <Route path="blog/how-to-choose-dentist-angel" element={<ChooseDentistArticle data-id="element-38" data-mp="30tat9nagl1yt" />} data-id="element-37" data-mp="30v3tajqkgnxu" />
        <Route path="sitemap" element={<SitemapPage data-id="element-40" data-mp="2vwtv009jp780" />} data-id="element-39" data-mp="2vw0x7oskds20" />
        <Route path="services" element={<ServicesPage data-id="element-42" data-mp="2qwob569iop0h" />} data-id="element-41" data-mp="2vug483lweh69" />
        <Route path="services/paediatric-dentistry" element={<PaediatricDentistryPage data-id="element-44" data-mp="2lymipuq7bdcc" />} data-id="element-43" data-mp="2qvwfjvfx6ra7" />
        <Route path="services/orthodontics" element={<OrthodonticsPage data-id="element-46" data-mp="2hw9ghganxxkb" />} data-id="element-45" data-mp="2lxuz7i9vp62f" />
        {/* Orthodontic Appliances — one localized slug per language */}
        <Route path="services/orthodontic-appliances" element={<OrthodonticAppliancesPage data-id="element-48" data-mp="286ptbyfx3hjn" />} data-id="element-47" data-mp="2d59fnzyfavcj" />
        <Route path="services/ortodontychni-aparaty" element={<OrthodonticAppliancesPage data-id="element-50" data-mp="23852uqqgwc8e" />} data-id="element-49" data-mp="287hq3cl93lbi" />
        <Route path="services/ortodonticheskie-apparaty" element={<OrthodonticAppliancesPage data-id="element-52" data-mp="1y78pzno51fqu" />} data-id="element-51" data-mp="239g68pg03hyz" />
        <Route path="services/aparate-ortodontice" element={<OrthodonticAppliancesPage data-id="element-54" data-mp="1u7qfllty0n29" />} data-id="element-53" data-mp="1u6x5qbzothfw" />
        <Route path="services/orthodontics/clear-aligners" element={<ClearAlignersPage data-id="element-56" data-mp="dqhmbjgh5wpv" />} data-id="element-55" data-mp="9pfnv4udtooy" />
        <Route path="services/orthodontics/invisalign-style-aligners" element={<InvisalignAlignersPage data-id="element-58" data-mp="ipb3bluz05us" />} data-id="element-57" data-mp="dpgednur9qcd" />
        <Route path="services/orthodontics/metal-braces" element={<MetalBracesPage data-id="element-60" data-mp="no5k6i9dnkc9" />} data-id="element-59" data-mp="inhsrvh8l1hi" />
        <Route path="services/orthodontics/ceramic-braces" element={<CeramicBracesPage data-id="element-62" data-mp="3m485et7bqcs6" />} data-id="element-61" data-mp="3m6b5k626xmam" />
        <Route path="services/orthodontics/retainers" element={<RetainersPage data-id="element-64" data-mp="3r31n6xtn8py5" />} data-id="element-63" data-mp="3r4utud49ba65" />
        <Route path="services/orthodontics/children" element={<ChildrensOrthodonticsPage data-id="element-66" data-mp="4pjhx024jn6r" />} data-id="element-65" data-mp="3w47b8icczp1c" />
        <Route path="services/:serviceId" element={<ServiceDetailPage data-id="element-68" data-mp="33ftd82l9ssps" />} data-id="element-67" data-mp="4re3mgl7zjbw" />
        <Route path="prices" element={<PricesPage data-id="element-70" data-mp="33fa51dlf9ioy" />} data-id="element-69" data-mp="33gl8fcaygofj" />
        <Route path="price-list" element={<PrintPricePage data-id="element-72" data-mp="38ed50jmjoyq2" />} data-id="element-71" data-mp="33dz2ti7mkizy" />
        <Route path="ai-support" element={<AiSupportPage data-id="element-74" data-mp="38cjv8vgmxydu" />} data-id="element-73" data-mp="38dbqg56blu3l" />
        <Route path="about" element={<AboutPage data-id="element-76" data-mp="2f6bwnhhq4jnk" />} data-id="element-75" data-mp="2f7n2rnyn63hu" />
        <Route path="patient-experience" element={<PatientExperiencePage data-id="element-78" data-mp="2k5ovl0lvdh6i" />} data-id="element-77" data-mp="2f6vdeuvn1200" />
        <Route path="results" element={<ResultsPage data-id="element-80" data-mp="2k81b7745nfge" />} data-id="element-79" data-mp="2k4x2pxjnpxjx" />
        <Route path="reviews" element={<ReviewsPage data-id="element-82" data-mp="2p6ciba32fbux" />} data-id="element-81" data-mp="2p5kreckey2az" />
        <Route path="contact" element={<ContactPage data-id="element-84" data-mp="2u4e6g9fcm1d9" />} data-id="element-83" data-mp="2p62qri2dgwn4" />
        <Route path="privacy-policy" element={<PrivacyPolicyPage data-id="element-86" data-mp="2u3mhhhg9b1vy" />} data-id="element-85" data-mp="2u4nvnuskk4hw" />
        <Route path="cookie-policy" element={<CookiePolicyPage data-id="element-88" data-mp="1whot53f742c3" />} data-id="element-87" data-mp="1wgnfcr6sp1qo" />
        <Route path="patient-privacy-notice" element={<PatientPrivacyNoticePage data-id="element-90" data-mp="21grzcr6wweln" />} data-id="element-89" data-mp="1wignyc0yxw1b" />
        <Route path="terms-and-conditions" element={<TermsAndConditionsPage data-id="element-92" data-mp="26e1oe5qe2o8y" />} data-id="element-91" data-mp="21ddwziy905e7" />
        <Route path="*" element={<Navigate to={`/${lang}`} replace data-id="element-94" data-mp="2b61pi1i73n74" />} data-id="element-93" data-mp="2b5jpctsewotx" />
      </Routes>
    </LanguageProvider>;
}
export function App() {
  return <BrowserRouter data-id="element-95" data-mp="1dpcvgtzhlp8a">
      <ScrollToTop data-id="element-96" data-mp="1dpw2vgrigv82" />
      <Routes data-id="element-97" data-mp="1dobey9zpcaj6">
        <Route path="/business-card" element={<BusinessCardPage data-id="element-99" data-mp="1inxu4oz3c0um" />} data-id="element-98" data-mp="1ioz8b2bel3gk" />
        <Route path="/business-card-new" element={<BusinessCardNewPage data-id="element-101" data-mp="3w00jbad95024" />} data-id="element-100" data-mp="1in4jve0xat7q" />
        <Route path="/business-card-new-ua" element={<BusinessCardNewUaPage data-id="element-103" data-mp="3rxnhuy5jfob5" />} data-id="element-102" data-mp="3vyyzohi6758s" />
        <Route path="/business-card-new-ru" element={<BusinessCardNewRuPage data-id="element-105" data-mp="3mxrcovl3tqu0" />} data-id="element-104" data-mp="3ryorcy6wo2qr" />
        <Route path="/gift-certificate" element={<GiftCertificate data-id="element-107" data-mp="3i094j7ewvfn2" />} data-id="element-106" data-mp="3n0x1satqgspq" />
        <Route path="/influencer-invite" element={<InfluencerInvitePage data-id="element-109" data-mp="3d0mnemvypf8z" />} data-id="element-108" data-mp="3hz7pkrury902" />
        <Route path="/raffle-voucher" element={<RaffleVoucherPage data-id="element-111" data-mp="3d1xuowom954u" />} data-id="element-110" data-mp="3d0wj8r3ougml" />
        <Route path="/whatsapp-qr" element={<WhatsAppQrPage data-id="element-113" data-mp="388v4myj1sdte" />} data-id="element-112" data-mp="38a6bx8bpc3p9" />
        <Route path="/logo" element={<LogoPage data-id="element-115" data-mp="348cfxdcgbnvf" />} data-id="element-114" data-mp="388mql3b6v2gm" />
        <Route path="/orthodontic-flyer-ua" element={<OrthodonticFlyerUaPage data-id="element-117" data-mp="345ymta1c0lqi" />} data-id="element-116" data-mp="347jfsv7zzwm9" />
        <Route path="/" element={<LanguageRedirect data-id="element-119" data-mp="2zb2r9zwvzg8n" />} data-id="element-118" data-mp="2zaatcifth6f7" />
        <Route path="/:lang/*" element={<LanguageRoutes data-id="element-121" data-mp="2uaeropuldr2t" />} data-id="element-120" data-mp="2ua59hofsgo7o" />
        <Route path="*" element={<LanguageRedirect data-id="element-123" data-mp="2ubzl2c5673z3" />} data-id="element-122" data-mp="2u9n7scacxhsd" />
      </Routes>
    </BrowserRouter>;
}